import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/InfoOutlined';

import ResponsiveSheet from './ResponsiveSheet';
import { DESCRIPTION_INFO } from '../../transaction/constants';
import { TOKENS } from '../theme';

// Description rules — keep in lockstep with Transaction.jsx so the sheet
// behaves identically. Lowercase comparison throughout.
const CUSTOMER_PICKER_DESCS = new Set([
  'customer loans',
  'zelle',
  'extra match / lucky',
  'customer pay loans',
]);
const EMPLOYEE_PICKER_DESCS = new Set([
  'employee short payment',
  'transfer money to employee',
  'transfer money from employee',
]);
const ZELLE_EMPLOYEE_DESCS = new Set(['zelle']);
const NAME_INPUT_DESCS = new Set(['manager loan to bank']);

// Bank/expense sheet — same field set + validation rules as Transaction.jsx
// (so the manager's edit experience matches the employee's create flow).
// kind: 'bank' | 'expense'
// mode: 'edit' | 'add'
export default function EditTransactionSheet({
  open,
  onClose,
  onSave,
  kind = 'bank',
  mode = 'edit',
  entry = {},
  categories = [],
  customers = [],
  employees = [],
}) {
  const isBank = kind === 'bank';
  const accent = isBank ? TOKENS.emerald600 : TOKENS.red600;
  const accentDark = isBank ? TOKENS.emerald700 : TOKENS.red700;

  // Description options — backend TransactionCategory records, with the
  // bank-side "TRANSFER MONEY FROM EMPLOYEE" appended as a synthetic if the
  // backend hasn't defined it. (Pre-existing "TRANSFER MONEY TO EMPLOYEE"
  // is assumed to already be a category record.)
  const descriptionOptions = useMemo(() => {
    const cats = Array.isArray(categories) ? categories : [];
    const has = (name) =>
      cats.some((c) => c.name?.toUpperCase() === name.toUpperCase());
    const extras = [];
    if (!has('TRANSFER MONEY FROM EMPLOYEE')) {
      extras.push({
        name: 'TRANSFER MONEY FROM EMPLOYEE',
        expenseType: null,
        isGlobal: true,
      });
    }
    return [...cats, ...extras];
  }, [categories]);

  // Rows produced by the rollover flow have personalised descriptions like
  // "TRANSFER MONEY TO MARIA" / "TRANSFER MONEY FROM JOHN". For the edit
  // form we collapse them back to the canonical category name so the
  // description Autocomplete matches and the dependent employee picker
  // shows. entry.selectedEmployeeId carries the other party for both sides.
  const initialDescription = (() => {
    const d = entry.description?.toUpperCase() || '';
    if (d.startsWith('TRANSFER MONEY TO ')) return 'TRANSFER MONEY TO EMPLOYEE';
    if (d.startsWith('TRANSFER MONEY FROM '))
      return 'TRANSFER MONEY FROM EMPLOYEE';
    return entry.description || '';
  })();
  const [description, setDescription] = useState(initialDescription);
  const [expenseType, setExpenseType] = useState(entry.expenseType || null);
  const [amount, setAmount] = useState(entry.amount || '');
  const [comments, setComments] = useState(entry.comments || '');
  const [nameInput, setNameInput] = useState(entry.nameInput || '');
  const [selectedCustomerId, setSelectedCustomerId] = useState(
    entry.selectedCustomerId || null
  );
  // Pre-fill the employee picker for personalized TRANSFER MONEY rows.
  // Newer rows store selectedEmployeeId; older rows (rollover-generated)
  // leave it null, so fall back to parsing the first name out of the
  // description and matching by firstname (case-insensitive).
  const initialEmployeeId = useMemo(() => {
    if (entry.selectedEmployeeId) return entry.selectedEmployeeId;
    const d = entry.description?.toUpperCase() || '';
    let firstName = null;
    if (d.startsWith('TRANSFER MONEY TO ')) {
      firstName = d.slice('TRANSFER MONEY TO '.length).trim();
    } else if (d.startsWith('TRANSFER MONEY FROM ')) {
      firstName = d.slice('TRANSFER MONEY FROM '.length).trim();
    }
    if (!firstName) return null;
    const list = Array.isArray(employees) ? employees : [];
    const match = list.find((e) => e.firstname?.toUpperCase() === firstName);
    return match?.userId || null;
  }, [entry.selectedEmployeeId, entry.description, employees]);
  const [selectedEmployeeId, setSelectedEmployeeId] =
    useState(initialEmployeeId);

  // If employees arrive after first render, re-run the firstname lookup.
  useEffect(() => {
    if (selectedEmployeeId) return;
    if (initialEmployeeId) setSelectedEmployeeId(initialEmployeeId);
  }, [initialEmployeeId, selectedEmployeeId]);
  const [showInfo, setShowInfo] = useState(false);

  const descLower = description?.toLowerCase() || '';
  const showCustomerPicker = CUSTOMER_PICKER_DESCS.has(descLower);
  const showNameInput = NAME_INPUT_DESCS.has(descLower);
  const showEmployeePicker = EMPLOYEE_PICKER_DESCS.has(descLower);
  const showZelleEmployeePicker = ZELLE_EMPLOYEE_DESCS.has(descLower);

  const selectedDescOption = useMemo(() => {
    if (!description) return null;
    return (
      descriptionOptions.find(
        (c) => c.name?.toUpperCase() === description.toUpperCase()
      ) || null
    );
  }, [description, descriptionOptions]);

  const selectedCustomer = useMemo(
    () =>
      selectedCustomerId
        ? customers.find((c) => c.customerId === selectedCustomerId) || null
        : null,
    [selectedCustomerId, customers]
  );

  const selectedEmployee = useMemo(
    () =>
      selectedEmployeeId
        ? employees.find((e) => e.userId === selectedEmployeeId) || null
        : null,
    [selectedEmployeeId, employees]
  );

  const employeeOptions = useMemo(() => {
    const list = Array.isArray(employees) ? employees : [];
    // For "TRANSFER MONEY FROM EMPLOYEE" the sender may have already clocked
    // out (manager is reconciling at shift end), so don't filter by isOnline.
    if (descLower === 'transfer money from employee') return list;
    return list.filter((e) => e.isOnline === true);
  }, [descLower, employees]);

  const zelleEmployeeOptions = useMemo(() => {
    return Array.isArray(employees) ? employees : [employees].filter(Boolean);
  }, [employees]);

  // Match Transaction.jsx's per-description validation. The button is
  // ENABLED only when canSave returns true.
  const canSave = useMemo(() => {
    if (!description || !amount || Number(amount) <= 0) return false;
    if (isBank) {
      if (descLower === 'customer loans') return false;
      if (descLower === 'extra match / lucky') return false;
      if (descLower === 'employee payroll') return false;
      if (descLower === 'transfer money to employee') return false;
      if (descLower === 'customer pay loans')
        return Boolean(selectedCustomerId);
      if (descLower === 'transfer money from employee')
        return Boolean(selectedEmployeeId);
      if (descLower === 'employee short payment')
        return Boolean(selectedEmployeeId);
      if (descLower === 'zelle')
        return !selectedEmployeeId && !selectedCustomerId;
      if (descLower === 'manager loan to bank') return Boolean(nameInput);
      return true;
    }
    // expense side
    if (descLower === 'customer loans') return Boolean(selectedCustomerId);
    if (descLower === 'transfer money to employee')
      return Boolean(selectedEmployeeId);
    if (descLower === 'transfer money from employee') return false;
    if (descLower === 'extra match / lucky') return Boolean(selectedCustomerId);
    if (descLower === 'customer pay loans') return false;
    if (descLower === 'zelle')
      return Boolean(selectedEmployeeId && selectedCustomerId);
    if (descLower === 'manager loan to bank') return false;
    if (descLower === 'employee short payment') return false;
    return true;
  }, [
    description,
    descLower,
    amount,
    isBank,
    selectedCustomerId,
    selectedEmployeeId,
    nameInput,
  ]);

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      description,
      expenseType,
      amount: String(amount),
      comments,
      nameInput,
      selectedCustomerId,
      selectedEmployeeId,
      type: isBank ? 'bank' : 'expense',
      amounts: entry.amounts || { 5: 0, 20: 0, 50: 0, 100: 0, 500: 0 },
    });
  };

  const infoText = description
    ? DESCRIPTION_INFO[description.toUpperCase()]
    : null;

  return (
    <ResponsiveSheet open={open} onClose={onClose}>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', maxHeight: 'inherit' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${TOKENS.slate200}`,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: accentDark,
              }}
            >
              {isBank ? 'Bank Entry' : 'Expense'}
            </Typography>
            <Typography
              sx={{ fontSize: 18, fontWeight: 700, color: TOKENS.slate900 }}
            >
              {mode === 'add' ? 'Add' : 'Edit'} {isBank ? 'Bank' : 'Expense'}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Field
            label="Description"
            adornment={
              description ? (
                <Tooltip title={showInfo ? 'Hide info' : 'View info'}>
                  <IconButton
                    size="small"
                    onClick={() => setShowInfo((s) => !s)}
                    sx={{ color: showInfo ? accentDark : TOKENS.slate500 }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null
            }
          >
            <Autocomplete
              size="small"
              options={descriptionOptions}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(a, b) =>
                a?.name?.toUpperCase() === b?.name?.toUpperCase()
              }
              value={selectedDescOption}
              onChange={(_e, value) => {
                setDescription(value ? value.name.toUpperCase() : '');
                setExpenseType(value?.expenseType || null);
                // Reset dependent fields when description changes so the
                // validation rules don't accidentally pass with stale data.
                setSelectedCustomerId(null);
                setSelectedEmployeeId(null);
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Pick a category…" />
              )}
            />
          </Field>

          {showInfo && infoText && (
            <Box
              sx={{
                px: 1.25,
                py: 1,
                borderRadius: 1,
                background: TOKENS.blue50,
                border: `1px solid ${TOKENS.blue200}`,
                borderLeft: `4px solid ${TOKENS.blue700}`,
              }}
            >
              <Typography sx={{ fontSize: 11, color: TOKENS.blue700 }}>
                <strong>Note:</strong> {infoText}
              </Typography>
            </Box>
          )}

          {showCustomerPicker && (
            <Field label="Customer *">
              <Autocomplete
                size="small"
                options={customers || []}
                getOptionLabel={(o) =>
                  `${o.firstName || ''} ${o.lastName || ''}`.trim()
                }
                isOptionEqualToValue={(a, b) => a.customerId === b.customerId}
                value={selectedCustomer}
                onChange={(_e, value) =>
                  setSelectedCustomerId(value ? value.customerId : null)
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select customer…" />
                )}
              />
            </Field>
          )}

          {showNameInput && (
            <Field label="Name *">
              <TextField
                fullWidth
                size="small"
                value={nameInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setNameInput(v.charAt(0).toUpperCase() + v.slice(1));
                }}
              />
            </Field>
          )}

          {showEmployeePicker && (
            <Field label="Employee *">
              <Autocomplete
                size="small"
                options={employeeOptions}
                getOptionLabel={(o) =>
                  [o.firstname, o.lastname].filter(Boolean).join(' ')
                }
                isOptionEqualToValue={(a, b) => a.userId === b.userId}
                value={selectedEmployee}
                onChange={(_e, value) =>
                  setSelectedEmployeeId(value ? value.userId : null)
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select employee…" />
                )}
              />
            </Field>
          )}

          {showZelleEmployeePicker && (
            <Field label="Funds deposited to account of *">
              <Autocomplete
                size="small"
                options={zelleEmployeeOptions}
                getOptionLabel={(o) =>
                  [o.firstname, o.lastname].filter(Boolean).join(' ')
                }
                isOptionEqualToValue={(a, b) => a.userId === b.userId}
                value={selectedEmployee}
                onChange={(_e, value) =>
                  setSelectedEmployeeId(value ? value.userId : null)
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select employee…" />
                )}
              />
            </Field>
          )}

          <Field label="Amount *">
            <TextField
              fullWidth
              size="small"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                sx: {
                  fontWeight: 700,
                  fontSize: 18,
                  color: accentDark,
                  fontFamily: 'monospace',
                },
              }}
            />
          </Field>

          <Field label="Comments">
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              maxRows={6}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Optional notes…"
            />
          </Field>
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderTop: `1px solid ${TOKENS.slate200}`,
            display: 'flex',
            gap: 1,
          }}
        >
          <Button fullWidth variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={!canSave}
            onClick={handleSave}
            sx={{ background: accent, '&:hover': { background: accentDark } }}
          >
            {mode === 'add'
              ? `Add ${isBank ? 'Bank' : 'Expense'}`
              : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </ResponsiveSheet>
  );
}

function Field({ label, adornment, children }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: TOKENS.slate500,
            flex: 1,
          }}
        >
          {label}
        </Typography>
        {adornment}
      </Box>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}
